import { Component, OnInit, Input } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule
} from '@angular/forms';
import { UserService } from 'src/app/services/user.service';
import { User } from 'src/app/models/User';
import { AuthService } from '../../../services/auth.service';
import { MessageService } from 'primeng/api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Card } from 'primeng/card';
import { PageTitleComponent } from '../../shared/page-title/page-title.component';
import { Message } from 'primeng/message';
import { RouterLink } from '@angular/router';
import { Password } from 'primeng/password';
import { ButtonDirective, ButtonLabel } from 'primeng/button';

@Component({
  selector: 'app-change-password',
  templateUrl: './change-password.component.html',
  styleUrls: ['./change-password.component.scss'],

  imports: [
    Card,
    PageTitleComponent,
    TranslatePipe,
    Message,
    RouterLink,
    ReactiveFormsModule,
    Password,
    ButtonDirective,
    ButtonLabel
  ]
})
export class ChangePasswordComponent implements OnInit {
  private admin: User | null;
  private user: User | null;

  public passwordForm: UntypedFormGroup;
  public errorMessage: string | undefined;
  public correctPassword = false;
  public canChangePassword = false;
  public passwordSuccessfullyChanged = false;
  @Input() currentUser?: User;

  /**
   * Fetch the current user and user whos password is about to change.
   * Check also if user has permission to change other users' password.
   * @private
   */
  private setUser(): void {
    this.admin = this.authService.getUserProfile();
    this.canChangePassword = this.authService.hasPermission([
      'update_password'
    ]);
    if (this.canChangePassword) {
      this.user = this.currentUser || this.admin;
    } else {
      this.errorMessage = this.translateService.instant(
        'common.password.no-rights'
      );
    }
  }

  constructor(
    private formBuilder: UntypedFormBuilder,
    private userService: UserService,
    private authService: AuthService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {
    this.admin = null;
    this.user = {} as User;
    this.passwordForm = this.formBuilder.group({
      oldPassword: ['', Validators.required],
      newPassword: ['', Validators.required],
      newPasswordAgain: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.setUser();
  }

  public submitOldPassword(): void {
    const pwForm = this.passwordForm.getRawValue();

    if (this.canChangePassword) {
      this.userService
        .checkUserPassword(this.admin!, pwForm.oldPassword)
        .subscribe(
          (result) => {
            this.correctPassword = result;
            this.errorMessage = undefined;
          },
          (error) => {
            this.errorMessage = this.translateService.instant(error.message);
          }
        );
    }
  }

  public submitNewPassword(): void {
    this.messageService.clear();
    const pwForm = this.passwordForm.getRawValue();
    if (pwForm.newPassword !== pwForm.newPasswordAgain) {
      this.errorMessage = this.translateService.instant(
        'common.password.passwords-do-not-match'
      );
      return;
    }
    if (pwForm.newPassword === pwForm.oldPassword) {
      this.errorMessage = this.translateService.instant(
        'common.password.password-should-be-new'
      );
      return;
    }
    this.errorMessage = undefined;
    this.userService
      .changeUserPassword(
        this.user!.id,
        this.admin!.email,
        pwForm.newPassword,
        pwForm.oldPassword
      )
      .subscribe((result) => {
        if (result) {
          this.correctPassword = false;

          this.passwordForm.setValue({
            oldPassword: '',
            newPassword: '',
            newPasswordAgain: ''
          });
          this.passwordSuccessfullyChanged = true;
        } else {
          this.errorMessage = this.translateService.instant(
            'common.password.password-should-be-new'
          );
        }
      });
  }
}
